import React from "react";
import { Flex, Text, Badge } from "components-lib";
import CountUp from "react-countup";

/**
 * StatsView Component
 * - Displays consultation statistics (total, incomplete, completed) by default
 * - If `pageType` is 'Bills' (case-insensitive) it displays billing statistics
 *   in the original badge-style UI: TOTAL REVENUE, PAID BILLS, PENDING BILLS, BOOKED.
 *
 * Billing props (optional):
 * - billTotalRevenue
 * - billPaid
 * - billPending
 * - billBooked
 *
 * Keeps original consultation display intact for other page types.
 */
const StatsView = ({
  total,
  pending,
  completed,
  pageType,
  // Optional billing props (used when pageType === 'Bills')
  billTotalRevenue = 0,
  billPaid = 0,
  billPending = 0,
  billBooked = 0,
}) => {
  // If this is the Bills view, render the four billing stats in badge format
  if (String(pageType || "").toLowerCase() === "bills") {
    return (
      <Flex gap={4} align="center" justify="flex-end" flexWrap="wrap">
        <Badge
          colorScheme="blue"
          color="white"
          variant={"solid"}
          borderRadius="md"
          px={2.5}
          py={2.5}
          fontSize="1em"
        >
          <Text fontSize="sm" fontWeight="600" color="white" textAlign="center">
            TOTAL REVENUE:
          </Text>
          <Text color="white" fontWeight="bold" fontSize="lg" mt={1}>
            ₹
            <CountUp
              start={0}
              end={Number(billTotalRevenue) || 0}
              duration={1}
              decimals={2}
              decimal="."
            />
          </Text>
        </Badge>

        <Badge
          colorScheme="green"
          variant={"solid"}
          color="white"
          borderRadius="md"
          px={2.5}
          py={2.5}
          fontSize="1em"
        >
          <Text fontSize="sm" fontWeight="600" color="white" textAlign="center">
            PAID BILLS:
          </Text>
          <Text color="white" fontWeight="bold" fontSize="lg" mt={1}>
            <CountUp start={0} end={Number(billPaid) || 0} duration={1} />
          </Text>
        </Badge>

        <Badge
          colorScheme="red"
          variant={"solid"}
          color="white"
          borderRadius="md"
          px={2.5}
          py={2.5}
          fontSize="1em"
        >
          <Text fontSize="sm" fontWeight="600" color="white" textAlign="center">
            PENDING BILLS:
          </Text>
          <Text color="white" fontWeight="bold" fontSize="lg" mt={1}>
            <CountUp start={0} end={Number(billPending) || 0} duration={1} />
          </Text>
        </Badge>

        <Badge
          colorScheme="purple"
          variant={"solid"}
          color="white"
          borderRadius="md"
          px={2.5}
          py={2.5}
          fontSize="1em"
        >
          <Text fontSize="sm" fontWeight="600" color="white" textAlign="center">
            BOOKED:
          </Text>
          <Text color="white" fontWeight="bold" fontSize="lg" mt={1}>
            <CountUp start={0} end={Number(billBooked) || 0} duration={1} />
          </Text>
        </Badge>
      </Flex>
    );
  }

  // Default: consultation stats (preserve original layout/behavior)
  return (
    <Flex gap={12} align="center" justify="flex-end" flexWrap="wrap">
      <Flex direction="column" align="center" textAlign="center">
        <Text fontSize="md" fontWeight="600" color="black" lineHeight="1.2">
          Total {pageType}
        </Text>
        <Text color="green.500" fontWeight="bold" fontSize="3xl" mt={1}>
          <CountUp start={0} end={total} duration={1} />
        </Text>
      </Flex>
      <Flex direction="column" align="center" textAlign="center">
        <Text fontSize="md" fontWeight="600" color="black" lineHeight="1.2">
          Incomplete
          {pageType}
        </Text>
        <Text color="orange.400" fontWeight="bold" fontSize="3xl" mt={1}>
          <CountUp start={0} end={pending} duration={1} />
        </Text>
      </Flex>
      <Flex direction="column" align="center" textAlign="center">
        <Text fontSize="md" fontWeight="600" color="black" lineHeight="1.2">
          Completed
          {pageType}
        </Text>
        <Text color="purple.500" fontWeight="bold" fontSize="3xl" mt={1}>
          <CountUp start={0} end={completed} duration={1} />
        </Text>
      </Flex>
    </Flex>
  );
};

export default StatsView;
